import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api, urlDeFoto, type Group, type Role, type User } from '../api/client';
import { useSession } from '../session/SessionContext';
import { Modal } from '../components/Modal';
import { SelectorDeGrupos } from '../components/SelectorDeGrupos';
import { AvisoError, AvisoExito } from '../components/Aviso';
import { PildoraEstado } from '../components/Pildora';
import { Paginacion } from '../components/Paginacion';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { PildoraRol } from '../components/PildoraRol';
import { FotoDePersona } from '../components/FotoDePersona';
import { AccionIcono } from '../components/AccionIcono';
import {
  IconoActivar,
  IconoActividad,
  IconoDesbloquear,
  IconoEnviar,
  IconoEscudo,
  IconoFoto,
  IconoLlave,
  IconoQuitarPersona,
  IconoRetirar,
} from '../components/Iconos';

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte.charAt(0))
    .join('')
    .toUpperCase();
}

const POR_PAGINA = 50;

export function Usuarios({ grupos }: { grupos: Group[] }) {
  const { t } = useTranslation();
  const { puede } = useSession();
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [invitado, setInvitado] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [gruposElegidos, setGruposElegidos] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<{
    usuario: User;
    accion: 'SUSPENDED' | 'TERMINATED';
  } | null>(null);
  const [cambiandoRol, setCambiandoRol] = useState<User | null>(null);
  const [reenviandoTodas, setReenviandoTodas] = useState(false);
  const [recuperando, setRecuperando] = useState<User | null>(null);
  const [recuperado, setRecuperado] = useState<string | null>(null);
  const [mensajeMasivo, setMensajeMasivo] = useState<string | null>(null);
  const [desbloqueado, setDesbloqueado] = useState<string | null>(null);
  const [fotoDe, setFotoDe] = useState<User | null>(null);

  /**
   * Página y búsqueda viven en la dirección, como en Documentos: se comparten, y
   * volver atrás regresa a la misma página.
   *
   * Antes se pedía sólo la primera página y el total se contaba sobre ella: con
   * la plantilla completa cargada la pantalla decía «25 en total» con 260
   * personas, y de la 26 en adelante no había forma de llegar a nadie.
   */
  const [parametros, setParametros] = useSearchParams();
  const pagina = Math.max(1, Number(parametros.get('page') ?? 1));
  const buscado = parametros.get('q') ?? '';
  const estado = parametros.get('status') ?? '';

  /** Un filtro se pone y se quita por la dirección: se comparte y vuelve atrás. */
  const fijar = (cambios: Record<string, string>) => {
    const siguientes = new URLSearchParams(parametros);
    for (const [clave, valor] of Object.entries(cambios)) {
      if (valor) siguientes.set(clave, valor);
      else siguientes.delete(clave);
    }
    // Cambiar un filtro devuelve a la primera página: la 4 de un filtro no es la
    // 4 del otro, y quedarse ahí muestra una tabla vacía que parece un error.
    siguientes.delete('page');
    setParametros(siguientes);
  };
  const [total, setTotal] = useState(0);
  const [invitados, setInvitados] = useState(0);
  const [porEstado, setPorEstado] = useState<Record<string, number>>({});
  const [totalPlantilla, setTotalPlantilla] = useState(0);

  const [texto, setTexto] = useState(buscado);
  useEffect(() => setTexto(buscado), [buscado]);
  useEffect(() => {
    if (texto.trim() === buscado) return;
    const esperar = window.setTimeout(() => fijar({ q: texto.trim() }), 350);
    return () => window.clearTimeout(esperar);
    // `fijar` se rehace en cada render; incluirlo dispararía la espera sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, buscado]);

  const cargar = useCallback(async () => {
    try {
      const consulta = new URLSearchParams({ page: String(pagina), pageSize: String(POR_PAGINA) });
      if (buscado) consulta.set('q', buscado);
      if (estado) consulta.set('status', estado);
      const [resultado, resumen, listaRoles] = await Promise.all([
        api.users(`?${consulta.toString()}`),
        // El conteo por estado es de toda la plantilla, no de esta página.
        api.usersSummary(),
        api.roles(),
      ]);
      setUsuarios(resultado.items);
      setTotal(resultado.totalItems);
      setInvitados(resumen.byStatus['INVITED'] ?? 0);
      setPorEstado(resumen.byStatus);
      setTotalPlantilla(resumen.total);
      setRoles(listaRoles);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [pagina, buscado, estado]);

  async function reenviar(usuario: User) {
    setOcupado(usuario.id);
    setError(null);
    try {
      await api.resendInvitation(usuario.id);
      setInvitado(usuario.username);
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  /**
   * Mandar el correo de recuperación. No cambia la contraseña ni la revela: la
   * persona la define en la pantalla del servidor de identidad, igual que en la
   * invitación. Se confirma antes porque manda un correo a alguien más.
   */
  async function recuperarContrasena(usuario: User) {
    setOcupado(usuario.id);
    setError(null);
    try {
      await api.sendPasswordReset(usuario.id);
      setRecuperando(null);
      setRecuperado(usuario.username);
      setInvitado(null);
    } catch (e) {
      setRecuperando(null);
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  /**
   * Cambiar el rol de una persona.
   *
   * Reemplaza, no agrega: en esta caja cada quien tiene un rol y verlo como una
   * lista invitaba a dejar dos puestos encima sin querer. El servidor impide
   * otorgar más de lo que uno tiene, así que aquí sólo se ofrecen los roles que
   * quien mira puede dar.
   */
  /** Reenviar a todos los que siguen esperando su invitación. */
  async function reenviarTodas() {
    setOcupado('todas');
    setError(null);
    try {
      const { scheduled } = await api.resendAllInvitations();
      setReenviandoTodas(false);
      setInvitado(null);
      setRecuperado(null);
      setMensajeMasivo(t('users.resendAllDone', { count: scheduled }));
    } catch (e) {
      setReenviandoTodas(false);
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  /**
   * Quitar el bloqueo de quien se equivocó tres veces de contraseña. Puede volver
   * a entrar con la contraseña que tenía; si no la recuerda, el camino sigue
   * siendo «Recuperar contraseña».
   */
  async function desbloquear(usuario: User) {
    setOcupado(usuario.id);
    setError(null);
    try {
      await api.unlockUser(usuario.id);
      setInvitado(null);
      setRecuperado(null);
      setDesbloqueado(usuario.fullName);
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  async function cambiarRol(usuario: User, roleId: string) {
    setOcupado(usuario.id);
    setError(null);
    try {
      await api.replaceUserRoles(usuario.id, roleId);
      setCambiandoRol(null);
      await cargar();
    } catch (e) {
      setCambiandoRol(null);
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  async function cambiarEstado(usuario: User, estado: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED') {
    setOcupado(usuario.id);
    setError(null);
    try {
      await api.setUserStatus(usuario.id, estado);
      setConfirmando(null);
      await cargar();
    } catch (e) {
      setConfirmando(null);
      setError(e);
    } finally {
      setOcupado(null);
    }
  }

  useEffect(() => {
    void cargar();
  }, [cargar]);


  async function invitar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const formulario = evento.currentTarget;
    const datos = new FormData(formulario);
    setEnviando(true);
    setError(null);
    setInvitado(null);

    try {
      const creado = await api.invite({
        fullName: String(datos.get('fullName')),
        email: String(datos.get('email')),
        username: String(datos.get('username')),
        roles: [{ roleId: String(datos.get('roleId')), scopeType: 'GLOBAL' }],
        groupIds: gruposElegidos,
      });
      setInvitado(creado.username);
      formulario.reset();
      setGruposElegidos([]);
      setAbierto(false);
      await cargar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('users.title')}</h1>
        <p className="pagina__bajada">{t('users.subtitle')}</p>
      </div>

      {!abierto && <AvisoError error={error} />}
      {invitado && <AvisoExito>{t('users.invited', { username: invitado })}</AvisoExito>}
      {recuperado && (
        <AvisoExito>{t('users.passwordResetSent', { username: recuperado })}</AvisoExito>
      )}
      {mensajeMasivo && <AvisoExito>{mensajeMasivo}</AvisoExito>}
      {desbloqueado && <AvisoExito>{t('users.unlocked', { name: desbloqueado })}</AvisoExito>}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('users.listTitle')}</h2>
          <span className="tarjeta__nota">
            {t('users.summary', { total, invited: invitados })}
          </span>
          {puede('user.manage') && (
            <a className="boton boton--fantasma" href={api.exportUsersUrl()}>
              {t('users.exportAction')}
            </a>
          )}
          {/* Reenviar a todos los pendientes. Sólo aparece cuando hay a quién:
              un botón que manda cero correos es un botón que enseña a dudar. */}
          {puede('user.invite') && invitados > 0 && (
            <button
              className="boton boton--fantasma"
              type="button"
              disabled={ocupado !== null}
              onClick={() => {
                setMensajeMasivo(null);
                setReenviandoTodas(true);
              }}
            >
              {t('users.resendAllAction', { count: invitados })}
            </button>
          )}
          {puede('user.invite') && (
            <button className="boton" type="button" onClick={() => setAbierto(true)}>
              {t('users.inviteAction')}
            </button>
          )}
        </div>

        {/* Los filtros van antes de la tabla y **fuera** de la condición: al
            filtrar a cero, la barra tiene que seguir ahí para poder deshacerlo. */}
        <BarraDeFiltros>
          <Buscador
            id="buscarPersona"
            etiqueta={t('users.search')}
            valor={texto}
            onCambio={setTexto}
          />
          <Segmentado
            etiqueta={t('users.status')}
            valor={estado}
            onCambio={(valor) => fijar({ status: valor })}
            opciones={[
              { valor: '', etiqueta: t('users.allStatuses'), conteo: totalPlantilla },
              { valor: 'ACTIVE', etiqueta: t('user.status.ACTIVE'), conteo: porEstado['ACTIVE'] },
              { valor: 'INVITED', etiqueta: t('user.status.INVITED'), conteo: porEstado['INVITED'] },
              { valor: 'SUSPENDED', etiqueta: t('user.status.SUSPENDED'), conteo: porEstado['SUSPENDED'] },
              { valor: 'TERMINATED', etiqueta: t('user.status.TERMINATED'), conteo: porEstado['TERMINATED'] },
            ]}
          />
          <div className="barra-filtros__acciones">
            <LimpiarFiltros
              hay={Boolean(estado || buscado)}
              onLimpiar={() => setParametros(new URLSearchParams())}
            />
          </div>
        </BarraDeFiltros>

        {usuarios.length === 0 ? (
          <div className="vacio">
            {/* Con un filtro puesto, «no hay nadie» sería falso: hay, pero no
                de esos. Y «invita a alguien» sería un consejo equivocado. */}
            <span className="vacio__titulo">
              {estado || buscado ? t('common.noResults') : t('users.empty')}
            </span>
            {!estado && !buscado && <span>{t('users.emptyHint')}</span>}
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('users.person')}</th>
                  <th>{t('users.username')}</th>
                  <th>{t('users.email')}</th>
                  <th title={t('users.roleColumnHelp')}>{t('users.roleColumn')}</th>
                  <th>{t('users.groups')}</th>
                  <th>{t('users.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {usuarios.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>
                      <span className="celda-usuario">
                        {usuario.photoUpdatedAt ? (
                          <img
                            className="avatar avatar--foto"
                            src={urlDeFoto(usuario.id, usuario.photoUpdatedAt) ?? undefined}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <span className="avatar" aria-hidden="true">
                            {iniciales(usuario.fullName)}
                          </span>
                        )}
                        {usuario.fullName}
                      </span>
                    </td>
                    <td className="mono">{usuario.username}</td>
                    <td className="mono">{usuario.email}</td>
                    <td>
                      <PildoraRol
                        codigos={usuario.roleCodes}
                        roles={roles}
                      />
                    </td>
                    <td className="mono">{usuario.groupIds.length}</td>
                    <td>
                      <PildoraEstado estado={usuario.status} />
                      {/* Bloqueada por intentos fallidos: la cuenta sigue activa,
                          pero no puede entrar hasta que alguien la desbloquee. */}
                      {usuario.locked && (
                        <span className="pildora pildora--critico" title={t('users.lockedHelp')}>
                          <span className="pildora__punto" aria-hidden="true" />
                          {t('users.locked')}
                        </span>
                      )}
                    </td>
                    <td className="acciones-fila">
                      {usuario.locked && puede('user.unlock') && (
                        <AccionIcono
                          etiqueta={t('users.unlockAction')}
                          icono={<IconoDesbloquear />}
                          tono="principal"
                          disabled={ocupado === usuario.id}
                          onClick={() => void desbloquear(usuario)}
                        />
                      )}
                      {/* Reenviar aparece sólo donde tiene sentido: en una cuenta
                          invitada que todavía no entra. Un botón que existe para
                          todos y sólo funciona para algunos enseña a no confiar
                          en ninguno. */}
                      {usuario.status === 'INVITED' && puede('user.invite') && (
                        <AccionIcono
                          etiqueta={t('users.resendAction')}
                          icono={<IconoEnviar />}
                          disabled={ocupado === usuario.id}
                          onClick={() => void reenviar(usuario)}
                        />
                      )}
                      {/* A una cuenta invitada se le reenvía la invitación, no
                          esto: todavía no tiene contraseña que recuperar. */}
                      {usuario.status === 'ACTIVE' && puede('user.invite') && (
                        <AccionIcono
                          etiqueta={t('users.passwordResetAction')}
                          icono={<IconoLlave />}
                          disabled={ocupado === usuario.id}
                          onClick={() => setRecuperando(usuario)}
                        />
                      )}
                      {/* La foto la pone quien administra, general o local: la
                          persona ya no se la pone sola. */}
                      {puede('user.photo') && usuario.status !== 'TERMINATED' && (
                        <AccionIcono
                          etiqueta={t('users.photoAction')}
                          icono={<IconoFoto />}
                          onClick={() => setFotoDe(usuario)}
                        />
                      )}
                      {puede('role.manage') && usuario.status !== 'TERMINATED' && (
                        <AccionIcono
                          etiqueta={t('users.changeRoleAction')}
                          icono={<IconoEscudo />}
                          disabled={ocupado === usuario.id}
                          onClick={() => setCambiandoRol(usuario)}
                        />
                      )}
                      {puede('user.manage') && usuario.status === 'ACTIVE' && (
                        <AccionIcono
                          etiqueta={t('users.disableAction')}
                          icono={<IconoRetirar />}
                          tono="peligro"
                          onClick={() => setConfirmando({ usuario, accion: 'SUSPENDED' })}
                        />
                      )}
                      {puede('user.manage') && usuario.status === 'SUSPENDED' && (
                        <AccionIcono
                          etiqueta={t('users.enableAction')}
                          icono={<IconoActivar />}
                          disabled={ocupado === usuario.id}
                          onClick={() => void cambiarEstado(usuario, 'ACTIVE')}
                        />
                      )}
                      {puede('user.manage') && usuario.status !== 'TERMINATED' && (
                        <AccionIcono
                          etiqueta={t('users.removeAction')}
                          icono={<IconoQuitarPersona />}
                          tono="peligro"
                          onClick={() => setConfirmando({ usuario, accion: 'TERMINATED' })}
                        />
                      )}
                      {puede('audit.read') && (
                        <AccionIcono
                          etiqueta={t('people.seeBehaviour')}
                          icono={<IconoActividad />}
                          to={`/usuarios/${usuario.id}`}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginacion total={total} porPagina={POR_PAGINA} />
          </div>
        )}
      </section>

      {confirmando && (
        <Modal
          titulo={t(
            confirmando.accion === 'SUSPENDED' ? 'users.confirmDisable' : 'users.confirmRemove',
            { name: confirmando.usuario.fullName },
          )}
          onCerrar={() => setConfirmando(null)}
        >
          <p>
            {t(
              confirmando.accion === 'SUSPENDED'
                ? 'users.confirmDisableHint'
                : 'users.confirmRemoveHint',
            )}
          </p>
          <div className="modal__acciones">
            <button
              className="boton boton--fantasma"
              type="button"
              onClick={() => setConfirmando(null)}
            >
              {t('groups.cancel')}
            </button>
            <button
              className="boton"
              type="button"
              disabled={ocupado === confirmando.usuario.id}
              onClick={() => void cambiarEstado(confirmando.usuario, confirmando.accion)}
            >
              {t(
                confirmando.accion === 'SUSPENDED'
                  ? 'users.disableAction'
                  : 'users.removeAction',
              )}
            </button>
          </div>
        </Modal>
      )}

      {reenviandoTodas && (
        <Modal titulo={t('users.resendAllConfirm')} onCerrar={() => setReenviandoTodas(false)}>
          {/* El número, grande y antes del botón: es un correo a cada una de
              esas personas y no se puede deshacer. */}
          <p>{t('users.resendAllConfirmHint', { count: invitados })}</p>
          <p className="campo__ayuda">{t('users.resendAllNote')}</p>
          <div className="modal__acciones">
            <button
              className="boton boton--fantasma"
              type="button"
              onClick={() => setReenviandoTodas(false)}
            >
              {t('common.cancel')}
            </button>
            <button
              className="boton"
              type="button"
              disabled={ocupado !== null}
              onClick={() => void reenviarTodas()}
            >
              {t('users.resendAllConfirmAction', { count: invitados })}
            </button>
          </div>
        </Modal>
      )}

      {cambiandoRol && (
        <Modal
          titulo={t('users.changeRoleTitle', { name: cambiandoRol.fullName })}
          onCerrar={() => setCambiandoRol(null)}
        >
          <p className="campo__ayuda">{t('users.changeRoleHint')}</p>
          <ul className="lista-opciones">
            {/* La misma regla que al invitar, y la misma que aplica el servidor:
                sólo los roles que no tienen ningún permiso que quien mira no
                tenga. Antes aquí decía «los privilegiados sólo para quien
                administra usuarios», que escondía «Administrador local» a un
                administrador local — un rol que sí puede otorgar. */}
            {roles
              .filter((rol) => rol.permissions.every((p) => puede(p)))
              .map((rol) => {
                const actual = cambiandoRol.roleCodes.includes(rol.code);
                return (
                  <li key={rol.id}>
                    <button
                      type="button"
                      className={`opcion-rol${actual ? ' opcion-rol--actual' : ''}`}
                      disabled={actual || ocupado === cambiandoRol.id}
                      onClick={() => void cambiarRol(cambiandoRol, rol.id)}
                    >
                      <span className="opcion-rol__nombre">{rol.name}</span>
                      {/* Lo que de verdad cambia para la persona: si se le va a
                          pedir un código al entrar a lo administrativo. */}
                      <span className="opcion-rol__nota">
                        {rol.isPrivileged
                          ? t('users.roleAsksOtp')
                          : t('users.roleNoOtp')}
                      </span>
                      {actual && <span className="opcion-rol__actual">{t('users.roleCurrent')}</span>}
                    </button>
                  </li>
                );
              })}
          </ul>
        </Modal>
      )}

      {fotoDe && (
        <FotoDePersona
          usuario={fotoDe}
          onCambio={() => void cargar()}
          onCerrar={() => setFotoDe(null)}
        />
      )}

      {recuperando && (
        <Modal
          titulo={t('users.passwordResetConfirm')}
          onCerrar={() => setRecuperando(null)}
        >
          <p>{t('users.passwordResetConfirmHint', { username: recuperando.fullName })}</p>
          <div className="modal__acciones">
            <button
              className="boton boton--fantasma"
              type="button"
              onClick={() => setRecuperando(null)}
            >
              {t('groups.cancel')}
            </button>
            <button
              className="boton"
              type="button"
              disabled={ocupado === recuperando.id}
              onClick={() => void recuperarContrasena(recuperando)}
            >
              {t('users.passwordResetConfirmAction')}
            </button>
          </div>
        </Modal>
      )}

      {/* Lo que no se puede hacer, no se muestra: sin el permiso no existe el formulario. */}
      {puede('user.invite') && abierto && (
        <Modal titulo={t('users.inviteTitle')} onCerrar={() => setAbierto(false)}>
          {/* El aviso va **dentro** del modal. Estaba fuera, y como al fallar el
              modal no se cierra —sólo se cierra al tener éxito— el error se
              dibujaba detrás y no se veía nunca. El mensaje era correcto desde
              siempre: «ese nombre de usuario ya está en uso» existía y estaba
              traducido. Simplemente quedaba tapado, y quien invitaba no tenía
              forma de saber si había pasado algo. */}
          <AvisoError error={error} />
          <p className="campo__ayuda" style={{ marginBottom: '.9rem' }}>
            {t('users.inviteExplanation')}
          </p>

          <form onSubmit={invitar} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="campos">
              <div className="campo">
                <label htmlFor="fullName">{t('users.fullName')}</label>
                <input id="fullName" name="fullName" required minLength={3} autoComplete="off" />
              </div>
              <div className="campo">
                <label htmlFor="username">{t('users.username')}</label>
                <input id="username" name="username" required minLength={3} autoComplete="off" />
              </div>
              <div className="campo">
                <label htmlFor="email">{t('users.email')}</label>
                <input id="email" name="email" type="email" required autoComplete="off" />
                <span className="campo__ayuda">{t('users.emailHelp')}</span>
              </div>
              <div className="campo">
                <label htmlFor="roleId">{t('users.role')}</label>
                <select id="roleId" name="roleId" required defaultValue="">
                  <option value="" disabled>
                    —
                  </option>
                  {/* Sólo los roles que quien invita puede otorgar: los que no
                      tienen ningún permiso que él no tenga. El servidor lo
                      comprueba igual; esto evita ofrecer una opción que va a
                      rebotar. */}
                  {roles
                    .filter((rol) => rol.permissions.every((p) => puede(p)))
                    .map((rol) => (
                      <option key={rol.id} value={rol.id}>
                        {rol.name}
                      </option>
                    ))}
                </select>
                <span className="campo__ayuda">{t('users.roleHelp')}</span>
              </div>
            </div>

            {/* Era una lista `<select multiple>` de cuatro filas y media: había
                que mantener Ctrl pulsado para elegir más de uno y un clic normal
                borraba lo ya marcado. Es el mismo selector del alta de
                documentos porque es la misma decisión —quién ve qué—, y verla
                distinta hace creer que hace otra cosa. */}
            <fieldset className="campo">
              <legend>{t('users.groups')}</legend>
              <span className="campo__ayuda">{t('users.groupsHelp')}</span>
              <SelectorDeGrupos
                grupos={grupos}
                elegidos={gruposElegidos}
                onCambio={setGruposElegidos}
                deshabilitado={enviando}
                avisoVacio={{ texto: t('users.groupsEmpty') }}
              />
            </fieldset>
            <div className="tarjeta__acciones">
              <button className="boton" type="submit" disabled={enviando}>
                {enviando ? t('users.sending') : t('users.inviteAction')}
              </button>
              <button
                className="boton boton--fantasma"
                type="button"
                disabled={enviando}
                onClick={() => setAbierto(false)}
              >
                {t('users.cancel')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
